import { useCallback, useEffect, useState } from 'react';
import useRequest from 'hooks/useRequest';

// Hook para gestionar los edificios de un cliente. Expone fetch/create/update/
// remove y mantiene el arreglo en estado local. Idempotente contra
// `/clientes/:idCliente/edificios` y `/edificios/:id` del backend de Fase 1.
// No usa Context porque los tabs de IDClientePage montan un solo componente a
// la vez; si en el futuro EdificiosPage y AscensoresPage necesitan compartir
// la lista, se eleva a un Provider.
export function useEdificios(idCliente) {
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { request } = useRequest();

  const refresh = useCallback(async () => {
    if (!idCliente) return [];
    setLoading(true);
    setError(null);
    try {
      const data = await request(`/clientes/${idCliente}/edificios`, 'GET');
      const rows = Array.isArray(data) ? data : [];
      setEdificios(rows);
      return rows;
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [idCliente, request]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = useCallback(
    async (payload) => {
      const res = await request(`/clientes/${idCliente}/edificios`, 'POST', payload);
      await refresh();
      return res;
    },
    [idCliente, request, refresh]
  );

  const update = useCallback(
    async (id, payload) => {
      const res = await request(`/edificios/${id}`, 'PUT', payload);
      await refresh();
      return res;
    },
    [request, refresh]
  );

  const remove = useCallback(
    async (id) => {
      await request(`/edificios/${id}`, 'DELETE');
      await refresh();
    },
    [request, refresh]
  );

  return { edificios, loading, error, refresh, create, update, remove };
}
